import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import Console from './Console';
import { reportBoot } from './utils/reloadDiagnostics';

reportBoot();

const EXTERNAL_EXTENSION_SCHEMES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
];

const isExternalExtensionError = ({ message = '', filename = '', stack = '' } = {}) => {
  const text = `${message}\n${filename}\n${stack}`;
  return EXTERNAL_EXTENSION_SCHEMES.some((scheme) => text.includes(scheme));
};

// Enhanced error boundary with detailed debugging
const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = React.useState(false);
  const [errorDetails, setErrorDetails] = React.useState(null);
  
  React.useEffect(() => {
    const handleError = (event) => {
      const details = {
        message: event.error?.message || event.message || 'Unknown error',
        filename: event.filename || 'Unknown file',
        lineno: event.lineno || 'Unknown line',
        stack: event.error?.stack || 'No stack trace available'
      };

      if (isExternalExtensionError(details)) {
        console.warn('Ignoring browser extension error outside Lumoz Console:', details);
        return;
      }

      console.error('🚨 Window Error Event:', {
        message: event.error?.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        stack: event.error?.stack
      });
      
      setErrorDetails(details);
      
      setHasError(true);
    };
    
    const handleUnhandledRejection = (event) => {
      const details = {
        message: `Promise Rejection: ${event.reason?.message || event.reason}`,
        filename: 'Promise rejection',
        lineno: 'N/A',
        stack: event.reason?.stack || 'No stack trace available'
      };

      if (isExternalExtensionError(details)) {
        console.warn('Ignoring browser extension promise rejection outside Lumoz Console:', details);
        event.preventDefault();
        return;
      }

      console.error('🚨 Unhandled Promise Rejection:', event.reason);
      
      setErrorDetails(details);
      
      setHasError(true);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  if (hasError) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)'
      }}>
        <div style={{
          maxWidth: '420px',
          padding: '24px',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          backgroundColor: 'var(--color-surface)',
          boxShadow: 'var(--shadow-lg)'
        }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '20px', lineHeight: 1.3 }}>
            Something went wrong
          </h2>
          <p style={{ margin: '0 0 20px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Please refresh the page. If the problem continues, contact support.
          </p>
          <button 
            onClick={() => {
              setHasError(false);
              setErrorDetails(null);
            }}
            style={{
              padding: '10px 15px',
              backgroundColor: 'var(--color-accent-secondary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

try {
  console.log('🚀 Starting Lumoz Console with error boundary...');
  console.log('📍 Current location:', window.location.href);

  // Set base font size based on environment
  // Production (console.lumoz.ai) uses 14px, localhost uses 16px
  const isProduction = window.location.hostname === 'console.lumoz.ai';
  document.documentElement.style.fontSize = isProduction ? '14px' : '16px';
  console.log(`📏 Base font size set to: ${isProduction ? '14px (production)' : '16px (localhost)'}`);

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  console.log('📦 Creating React root...');
  const root = ReactDOM.createRoot(rootElement);

  console.log('🎨 Rendering Console component...');
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <Console />
      </ErrorBoundary>
    </React.StrictMode>
  );
  console.log('✅ Console render initiated successfully');
} catch (error) {
  console.error('❌ Failed to render console:', error);
  // Fallback: render directly to body if root element issue
  document.body.innerHTML = `
    <div style="padding: 20px; background: var(--color-status-danger); color: var(--color-text-inverse);">
      <h1>Console Render Error</h1>
      <p>Error: ${error.message}</p>
      <pre>${error.stack}</pre>
    </div>
  `;
}
